import {CMP} from "../cmp/cmp";
import {
    IAddtlConsentConfig,
    IAnalyticConfig,
    IConsentModeConfig, IInsistModeConfig,
    IParamGroup,
    ISite as ISiteInfo
} from "services-comun-meteored/modules/services/cmp/web/resources";
import {TestABGroup} from "./test-a-b-group";
import {TestAB} from "./test-a-b";
import {TestSession} from "../session/test-session";
import {TAnalyticProvider, TConsentModeMode} from "services-comun-meteored/modules/services/cmp/interface";
import {
    TParamAddtlConsent,
    TParamAnalytic,
    TParamConsentMode,
    TParamGroup, TParamInsistMode
} from "services-comun-meteored/modules/services/cmp/backend/interface";

export class Site {
    /* STATIC */
    public static load(): Site {
        return new Site((window as any).__cmpsite);
    }

    /* INSTANCE */
    protected constructor(private readonly _data: ISiteInfo) {
    }

    protected get data(): ISiteInfo {
        return this._data;
    }

    public get domain(): string {
        return this.data.p;
    }

    public get hash(): string {
        return this.data.h;
    }

    public get country(): string {
        return this.data.c;
    }

    public get language(): string {
        return this.data.l;
    }

    public get requestedConfig(): string|undefined {
        return this.data.rc;
    }

    public get config(): string {
        return this.data.cf;
    }

    public get defaultConsentString(): string {
        return this.data.cs;
    }

    public get defaultAddtlConsent(): string {
        return this.data.ac;
    }

    public get defaultCustomConsent(): string|undefined {
        return this.data.cc;
    }

    public hashCustomConfig(): boolean {
        return this.defaultCustomConsent !== undefined;
    }

    public gdprTemplate(): string|undefined {
        const testsAB = this.testABApplied();
        for (const test in testsAB) {
            const template = testsAB[test].spec.template;
            if (template) {
                return template['1']?.c;
            }
        }

        return this.data.t?.['1']?.c;
    }

    public gdprCSS(): string|undefined {
        const testsAB = this.testABApplied();
        for (const test in testsAB) {
            const template = testsAB[test].spec.template;
            if (template) {
                return template['1']?.s;
            }
        }
        return this.data.t?.['1']?.s;
    }

    private params(group: TParamGroup): IParamGroup|undefined {
        const salida: IParamGroup = {};
        if (this.data.pg?.[group]) {
            for (const key in this.data.pg?.[group]) {
                salida[key] = this.data.pg[group]![key];
            }
        }
        const testsAB = this.testABApplied();

        for (const test in testsAB) {
            const paramGroup = testsAB[test].spec.param_groups;
            if (paramGroup && paramGroup[group]) {
                for (const key in paramGroup[group]) {
                    salida[key] = paramGroup[group]![key];
                }
            }
        }
        return salida??undefined;

    }

    public gdprParams(): IParamGroup|undefined {
        return this.params(TParamGroup.GDPR);
    }

    public cmpParams(): IParamGroup|undefined {
        return this.data.pg?.[TParamGroup.CMP];
    }

    public insistParams(): IParamGroup|undefined {
        return this.params(TParamGroup.INSIST);
    }

    public numVendors(): number {
        return this.data.nv;
    }

    public langKey(): string {
        return this.data.lk;
    }

    public analyticConfig(): IAnalyticConfig|undefined {
        const params = this.params(TParamGroup.ANALYTIC);
        return params ? {
            e: params[TParamAnalytic.ENABLED] == '1',
            p: params[TParamAnalytic.PROVIDER] as TAnalyticProvider|undefined
        }: undefined;
    }

    public consentModeConfig(): IConsentModeConfig|undefined {
        const params = this.params(TParamGroup.CONSENT_MODE);
        return params ? {
            e: params[TParamConsentMode.ENABLED] == '1',
            m: params[TParamConsentMode.MODE] as TConsentModeMode|undefined
        }: undefined;
    }

    public insistModeConfig(): IInsistModeConfig|undefined {
        const params = this.params(TParamGroup.INSIST_MODE);
        return params ? {
            e: params[TParamInsistMode.ENABLED] == '1',
            sae: params[TParamInsistMode.SECONDARY_PLATFORM_ACTION_ENABLED] == '1',
            da: params[TParamInsistMode.INSIST_MODE_DIRECT_APPROVE] == '1'
        }: undefined;
    }

    public addtlConsentParams(): IAddtlConsentConfig|undefined {
        const params = this.params(TParamGroup.ADDTL_CONSENT);
        return params ? {
            v: parseInt(params[TParamAddtlConsent.VERSION])
        }: undefined;
    }

    private testABGroups(): TestABGroup[] {
        return this.data.tabg?.map(TestABGroup.build) ?? [];
    }

    public testABApplied(): TestAB[] {
        const factor = TestSession.get().value;
        return this.testABGroups().map(group => group.appliedTestAB(factor));
    }
}

let _instance: CMP|null = null;
export const site: Site = Site.load();

export const initiated = () => {
    return _instance != null;
}

export const instantiate = (instance: CMP) => {
    _instance = instance;
}

export default () => {
    if (_instance == null) {
        throw new Error("CMP not instantiated");
    }
    return _instance;
}
